import { TrendingUp, TrendingDown } from "lucide-react";

// Mobile-only card list for Portfolio Holdings — replaces the wide multi-column
// table on phone screens. Each card stacks the key metrics so the user only
// scrolls vertically (no horizontal scroll).
const fmtINR = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};
const fmtCompact = (v) => {
  const abs = Math.abs(v || 0);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${fmtINR(v)}`;
};

const MobileHoldingCard = ({ holding, holdingsPct }) => {
  const cost = parseFloat(holding.cost_value) || 0;
  const mv = parseFloat(holding.market_value) || 0;
  const pl = mv - cost;
  const plPct = cost > 0 ? (pl / cost) * 100 : 0;
  const isPositive = pl >= 0;
  const xirr = holding.xirr;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm active:scale-[0.99] transition-transform">
      {/* Top row: scheme name + market value */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
            {holding.scheme_name || "—"}
          </p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Folio {holding.folio_no || "—"}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-bold text-gray-900">{fmtCompact(mv)}</p>
          <div className={`inline-flex items-center gap-0.5 text-[11px] font-semibold mt-0.5 ${isPositive ? "text-green-700" : "text-red-700"}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {isPositive ? "+" : ""}{plPct.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Invested</p>
          <p className="text-xs font-medium text-gray-800 mt-0.5">{fmtCompact(cost)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500">P/L</p>
          <p className={`text-xs font-medium mt-0.5 ${isPositive ? "text-green-700" : "text-red-700"}`}>
            {isPositive ? "+" : ""}{fmtCompact(pl)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500">XIRR</p>
          <p className="text-xs font-medium text-gray-800 mt-0.5">
            {xirr != null && !isNaN(xirr) ? `${parseFloat(xirr).toFixed(2)}%` : "—"}
          </p>
        </div>
      </div>

      {/* Footer chips */}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
          {(parseFloat(holding.units) || 0).toFixed(3)} units
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">
          {holdingsPct}% of folio
        </span>
        {holding.source && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 uppercase">
            {String(holding.source).includes("WBR") ? "CAMS" : "Karvy"}
          </span>
        )}
      </div>
    </div>
  );
};

const MobileHoldingsCards = ({ rows = [], totalMarketValue = 0 }) => {
  if (!rows.length) return null;
  return (
    <div className="space-y-2.5 px-1 pb-3" data-testid="mobile-holdings-cards">
      {rows.map((holding, index) => {
        const pct = totalMarketValue > 0
          ? ((holding.market_value || 0) / totalMarketValue * 100).toFixed(1)
          : "0.0";
        return (
          <MobileHoldingCard
            key={`${holding.folio_no}-${holding.isin}-${index}`}
            holding={holding}
            holdingsPct={pct}
          />
        );
      })}
    </div>
  );
};

export default MobileHoldingsCards;
